import React from "react"
import { Pressable, StyleSheet } from "react-native"
import { useStyle } from "react-native-style-utilities"

import { setActiveFarmId } from "@/base/async-storage"
import {
  MAP_OVERLAY_STYLES,
  MAP_OVERLAY_TOKENS,
  useMapOverlayStyle,
} from "@/components/map-overlay-controls"
import { useErrorHandler } from "@/hooks/useErrorHandler"
import { setActiveFeature } from "@/redux/actions"
import {
  getActiveFarmNameFromState,
  getDemoFarmIdOrActiveFarmIdFromState,
} from "@/redux/selectors"
import { useRootDispatch } from "@/redux/useRootDispatch"
import {
  useRootSelector,
  useShallowEqualSelector,
} from "@/redux/useRootSelector"
import { getIsChangingActiveFarmFromState } from "@/redux/user-farms.reducer"
import { getAllRealFarmsFromState } from "@/redux/user-farms.selectors"
import { AppIcons, Box, CycleButton, Row } from "@/theme"
import { AppText } from "@/theme/Text"
import { getFontName } from "@/theme/theme/fonts"
import { SPACING } from "@/theme/theme/spacing"

import type { IconButtonProps, RowProps } from "@/theme"
import type { StyleProp, ViewStyle } from "react-native"

/**
 * Farm cycling buttons (left/right arrows) for switching between farms
 */
export function ActiveFarmCycler({
  _buttonProps,
  isChangingFarms,
  ...rest
}: RowProps & {
  _buttonProps?: Omit<IconButtonProps, "IconComponent">
  isChangingFarms: boolean
}): React.JSX.Element {
  const dispatch = useRootDispatch()
  const handleError = useErrorHandler()
  const handleChange = (farm: { id: number }) => {
    dispatch(setActiveFarmId(farm.id)).unwrap().catch(handleError)
  }
  const adjacentFarmIds = useShallowEqualSelector((state) => {
    const allFarms = getAllRealFarmsFromState(state)
    const activeFarmId = getDemoFarmIdOrActiveFarmIdFromState(state)
    const activeFarmIndex = allFarms.findIndex(
      (nextFarm) => nextFarm.id === activeFarmId,
    )
    const lastIndex = allFarms.length - 1
    let leftIndex = activeFarmIndex - 1
    let rightIndex = activeFarmIndex + 1

    if (leftIndex < 0) {
      leftIndex = lastIndex
    }
    if (rightIndex > lastIndex) {
      rightIndex = 0
    }

    return {
      left: allFarms[leftIndex],
      right: allFarms[rightIndex],
    }
  })

  const variant = "subtle"
  return (
    <Row id={"changeFarmAccount"} py={"$2"} {...rest}>
      <Box mr={"$2"}>
        <CycleButton
          key={"left"}
          isDisabled={isChangingFarms}
          variant={variant}
          onPress={() => {
            if (adjacentFarmIds.left) {
              handleChange(adjacentFarmIds.left)
            }
          }}
          {..._buttonProps}
          IconComponent={"ArrowLeft"}
        />
      </Box>
      <CycleButton
        key={"right"}
        id={"cycleRightBtn"}
        isDisabled={isChangingFarms}
        variant={variant}
        onPress={() => {
          if (adjacentFarmIds.right) {
            handleChange(adjacentFarmIds.right)
          }
        }}
        {..._buttonProps}
        IconComponent={"ArrowRight"}
      />
    </Row>
  )
}

interface ActiveFarmIndicatorProps {
  style?: StyleProp<ViewStyle>
}

/**
 * Active farm indicator for the status map.
 * Uses consistent map overlay control styling with pill button appearance.
 */
export function ActiveFarmIndicator({
  style,
}: ActiveFarmIndicatorProps): React.JSX.Element | null {
  const activeFarmName = useRootSelector(getActiveFarmNameFromState)
  const isChangingFarms = useRootSelector(getIsChangingActiveFarmFromState)
  const dispatch = useRootDispatch()
  const themeStyles = useMapOverlayStyle()

  // All hooks must be called before any early returns
  const baseStyle = useStyle(
    () => [
      MAP_OVERLAY_STYLES.buttonBase,
      MAP_OVERLAY_STYLES.pillButton,
      styles.farmButton,
      {
        backgroundColor: themeStyles.backgroundColor,
        borderColor: themeStyles.borderColor,
      },
      style,
    ],
    [themeStyles.backgroundColor, themeStyles.borderColor, style],
  )

  const textStyle = useStyle(
    () => ({
      color: themeStyles.iconColorActive,
      fontSize: 13,
      maxWidth: 160,
    }),
    [themeStyles.iconColorActive],
  )

  const onPress = () => {
    dispatch(setActiveFeature("changeFarmAccount"))
  }

  if (typeof activeFarmName === "undefined") {
    return null
  }

  return (
    <Pressable
      accessible
      accessibilityLabel={`Active farm: ${activeFarmName}. Tap to change.`}
      accessibilityRole="button"
      accessibilityState={{ disabled: isChangingFarms }}
      disabled={isChangingFarms}
      onPress={onPress}
      style={({ pressed }) => [
        baseStyle,
        pressed && {
          backgroundColor: themeStyles.pressedBackgroundColor,
          ...MAP_OVERLAY_STYLES.pressed,
        },
        isChangingFarms && { opacity: 0.5 },
      ]}
    >
      <AppText
        fontFamily={getFontName("Poppins_600SemiBold")}
        numberOfLines={1}
        style={textStyle}
      >
        {activeFarmName}
      </AppText>
      <AppIcons.ChevronDown
        color={themeStyles.iconColor}
        size={16}
      />
    </Pressable>
  )
}

const styles = StyleSheet.create({
  farmButton: {
    gap: SPACING.$1,
    height: MAP_OVERLAY_TOKENS.buttonSize,
    paddingHorizontal: SPACING.$3,
  },
})
